/**
 * Shell autenticado: `/w/:slug`.
 *
 * Resolve o slug -> workspace + papel UMA vez e entrega isso para a árvore
 * inteira. Também é aqui que se decide o que fazer quando não há workspace
 * (onboarding) e quando o slug não existe (tela honesta, não 404 genérico).
 */
import { useCallback, useMemo } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { LogOut, Settings } from 'lucide-react';
import { AppShell, Button, Card, ErrorState, FormAlert, StatusPill } from '@mgdlead/ui';
import { useQueryClient } from '@tanstack/react-query';
import type { BreadcrumbItem } from '@mgdlead/ui';
import { can } from '@mgdlead/shared';

import { BrandMark } from '@mgdlead/ui';
import { RouteLoading } from '../../auth/guards';
import { useSession } from '../../auth/useSession';
import { WorkspaceProvider } from '../../features/workspace/WorkspaceContext';
import {
  NAVIGATION,
  buildBottomNav,
  buildNavigation,
  navItemById,
  resolveActiveId,
  workspacePath,
} from '../../features/workspace/navigation';
import { monitorsSummary } from '../../features/monitor/monitor-ui';
import { useWorkspaceStream } from '../../lib/realtime';
import { incidentBadge } from '../../features/incident/incident-ui';
import { RelogioDoWorkspace } from '../../features/workspace/RelogioDoWorkspace';
import { ExperienceChrome } from '../../features/experience/ExperienceChrome';
import { ExperienceProvider } from '../../features/experience/ExperienceProvider';
import { ExperienceTopbar } from '../../features/experience/ExperienceTopbar';
import { MetaSemVinculoBanner } from '../../features/campaigns/MetaSemVinculo';
import { ScreenHint } from '../../features/experience/ScreenHint';
import {
  toWorkspaceRole,
  useIncidentCounts,
  useInvalidateIncidents,
  useMonitors,
  useTRPC,
  useWorkspaces,
} from '../../lib/api';
import { signOut } from '../../lib/auth';
import { humanizeError } from '../../lib/errors';

export function WorkspaceLayout() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSession();
  const workspacesQuery = useWorkspaces();

  const workspaces = workspacesQuery.data;

  const current = useMemo(
    () => workspaces?.find((workspace) => workspace.slug === slug),
    [workspaces, slug],
  );

  /**
   * O contador de incidentes do menu.
   *
   * Fica AQUI, antes de qualquer saída antecipada, porque hook não pode ser condicional —
   * e `current` já está resolvido neste ponto (o `useMemo` acima). Com workspace ainda
   * indefinido a consulta nem sai: `useIncidentCounts` desliga com id nulo.
   *
   * Quem não tem `incident:read` não vê o item no menu; pedir a contagem para essa pessoa
   * seria um 403 no console a cada navegação.
   */
  const incidentRole = current ? toWorkspaceRole(current.role) : null;
  const countsQuery = useIncidentCounts(
    current?.id ?? null,
    incidentRole !== null && can(incidentRole, 'incident:read'),
  );
  // `undefined` enquanto não chegou: o menu não mostra `0` por conta própria (invariante 8).
  const navBadges = useMemo(() => {
    const badge = incidentBadge(countsQuery.data);
    return badge ? { incidentes: badge } : {};
  }, [countsQuery.data]);

  /**
   * O fluxo de eventos do workspace, ligado no LAYOUT e não na tela de incidentes.
   *
   * Quem precisa saber que um incidente abriu é justamente quem NÃO está olhando a tela de
   * incidentes — o contador do menu é o único sinal que alcança quem está no dashboard às
   * 23h. Ligar o stream dentro da página faria o aviso existir só para quem já está vendo
   * o problema.
   *
   * A reação é invalidar, não remendar a lista com o que veio no evento: o evento é
   * enxuto (id, tipo, severidade, título) e a tela mostra mais que isso. Remendar seria
   * manter duas verdades, e a do servidor é a que vale (invariante 5). O custo é uma
   * consulta por evento — e evento de incidente é raro por definição.
   */
  const invalidateIncidents = useInvalidateIncidents();
  const podeOuvir = incidentRole !== null && can(incidentRole, 'incident:read');
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  // Incidente de número fora do ar muda também a lista de monitoradores — é ela que
  // acende e apaga a faixa vermelha do topo.
  const onStreamEvent = useCallback(() => {
    invalidateIncidents();
    void queryClient.invalidateQueries({ queryKey: trpc.monitor.list.pathKey() });
  }, [invalidateIncidents, queryClient, trpc]);
  useWorkspaceStream(current?.id ?? '', {
    onEvent: onStreamEvent,
    enabled: podeOuvir,
  });

  const handleSignOut = useCallback(() => {
    void signOut().finally(() => navigate('/login', { replace: true }));
  }, [navigate]);

  if (workspacesQuery.isPending) return <RouteLoading />;

  if (workspacesQuery.isError) {
    return (
      <div className="min-h-dvh bg-background p-4">
        <Card className="mx-auto mt-16 max-w-md">
          <ErrorState
            title="Não conseguimos carregar seus workspaces"
            description={humanizeError(workspacesQuery.error)}
            onRetry={() => void workspacesQuery.refetch()}
          />
        </Card>
      </div>
    );
  }

  // conta sem workspace nenhum: o lugar dela é o onboarding, não um painel vazio
  if (!workspaces || workspaces.length === 0) return <Navigate to="/onboarding" replace />;

  if (!current || slug === undefined) {
    const first = workspaces[0];
    return (
      <div className="min-h-dvh bg-background p-4">
        <Card className="mx-auto mt-16 max-w-md">
          <ErrorState
            title="Workspace não encontrado"
            description="O endereço aponta para um workspace que não existe ou ao qual você não tem mais acesso."
            detail={slug}
          />
          {first ? (
            <div className="mt-2 flex justify-center">
              <Button variant="primary" onClick={() => void navigate(`/w/${first.slug}`)}>
                Ir para {first.name}
              </Button>
            </div>
          ) : null}
        </Card>
      </div>
    );
  }

  // a api declara `role: string` na listagem; conferir antes de decidir menu
  const role = toWorkspaceRole(current.role);
  const activeId = resolveActiveId(location.pathname, current.slug);
  const activeItem = activeId === undefined ? undefined : navItemById(activeId);
  const activeGroup = NAVIGATION.find((group) => group.items.some((item) => item.id === activeId));

  const breadcrumbs: BreadcrumbItem[] = [
    { label: current.name, href: `/w/${current.slug}` },
    ...(activeGroup?.label ? [{ label: activeGroup.label }] : []),
    ...(activeItem && activeItem.id !== 'dashboard' ? [{ label: activeItem.label }] : []),
  ];
  if (breadcrumbs.length === 1) breadcrumbs.push({ label: 'Dashboard' });

  /** `g d`, `g c`… vão para o item de menu — e só se o papel enxerga o item. */
  const goToNav = (navId: string) => {
    const item = buildNavigation(current.slug, role)
      .flatMap((group) => group.items)
      .find((candidate) => candidate.id === navId);
    if (item) void navigate(item.href);
  };

  return (
    <WorkspaceProvider workspace={current} workspaces={workspaces}>
      <ExperienceProvider onGo={goToNav}>
        <AppShell
          navigation={buildNavigation(current.slug, role, navBadges)}
          bottomNav={buildBottomNav(current.slug, role, navBadges)}
          {...(activeId !== undefined ? { activeId } : {})}
          brand={
            <Link
              to={`/w/${current.slug}`}
              className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <BrandMark />
            </Link>
          }
          sidebarFooter={<MonitorsFooter workspaceId={current.id} />}
          renderLink={({ item, props, children }) => {
            const { href: _href, ...rest } = props;
            return (
              <Link to={item.href} {...rest}>
                {children}
              </Link>
            );
          }}
          renderBottomLink={({ item, props, children }) => {
            const { href: _href, ...rest } = props;
            return (
              <Link to={item.href} {...rest}>
                {children}
              </Link>
            );
          }}
          topbar={({ onOpenMenu }) => (
            <ExperienceTopbar
              onOpenMenu={onOpenMenu}
              breadcrumbs={breadcrumbs}
              workspaces={workspaces.map((workspace) => ({
                id: workspace.slug,
                name: workspace.name,
              }))}
              activeWorkspaceId={current.slug}
              {...(podeOuvir
                ? {
                    notifications: {
                      onClick: () => void navigate(workspacePath(current.slug, 'incidentes')),
                      unread: (countsQuery.data?.open ?? 0) > 0,
                    },
                  }
                : {})}
              onWorkspaceChange={(nextSlug) => void navigate(`/w/${nextSlug}`)}
              {...(user
                ? { user: { name: user.name, email: user.email, avatarUrl: user.image } }
                : {})}
              userActions={[
                {
                  id: 'configuracoes',
                  label: 'Configurações',
                  icon: <Settings />,
                  onSelect: () => void navigate(workspacePath(current.slug, 'configuracoes')),
                },
                {
                  id: 'sair',
                  label: 'Sair da conta',
                  icon: <LogOut />,
                  onSelect: handleSignOut,
                  destructive: true,
                },
              ]}
            >
              <RelogioDoWorkspace
                timezone={current.timezone}
                configuracoesHref={workspacePath(current.slug, 'configuracoes')}
              />
            </ExperienceTopbar>
          )}
        >
          <MonitorDownBanner workspaceId={current.id} slug={current.slug} />
          <MetaSemVinculoBanner workspaceId={current.id} enabled={can(role, 'campaign:update')} />
          <ScreenHint screenId={activeId} />
          <Outlet />
        </AppShell>
        <ExperienceChrome workspaceId={current.id} slug={current.slug} role={role} />
      </ExperienceProvider>
    </WorkspaceProvider>
  );
}

/**
 * Status agregado dos números, no rodapé da barra lateral.
 *
 * Componente separado porque o layout tem saídas antecipadas (carregando, slug inexistente)
 * e o hook só pode existir depois de haver workspace — e porque assim o rodapé lê o MESMO
 * `monitor.list` que a tela de monitoradores, já em cache, sem uma segunda rota só para ele.
 *
 * Enquanto a lista não chegou, o rodapé não aparece: um "Nenhum número conectado" piscando
 * no primeiro quadro é a mesma mentira que este componente veio desfazer.
 */
function MonitorsFooter({ workspaceId }: { workspaceId: string }) {
  const monitorsQuery = useMonitors(workspaceId);
  const monitors = monitorsQuery.data?.monitors;

  if (monitors === undefined) return null;

  const resumo = monitorsSummary(monitors);
  return <StatusPill status={resumo.status} label={resumo.label} />;
}

/**
 * A faixa vermelha de "número caiu", no topo de TODAS as telas (pedido do dono, 06/10).
 *
 * "Caiu" = já esteve conectado (`lastConnectedAt`), tem grupo para vigiar e não está
 * conectado agora. Número novo esperando o primeiro QR não é queda — é configuração.
 * Lê o MESMO `monitor.list` do rodapé e da tela de monitoradores, já em cache.
 */
function MonitorDownBanner({ workspaceId, slug }: { workspaceId: string; slug: string }) {
  const monitors = useMonitors(workspaceId).data?.monitors;
  if (!monitors) return null;
  const caidos = monitors.filter(
    (m) =>
      m.archivedAt === null &&
      m.lastConnectedAt !== null &&
      m.groupsCount > 0 &&
      m.status !== 'CONNECTED',
  );
  if (caidos.length === 0) return null;

  const nomes = caidos.map((m) => `“${m.name}”`).join(', ');
  return (
    <div role="alert" className="mb-4">
      <FormAlert
        tone="danger"
        title={caidos.length === 1 ? `O número ${nomes} caiu` : `Os números ${nomes} caíram`}
      >
        <span className="block">
          Enquanto não reconectar, as entradas e saídas dos grupos não estão sendo registradas. Ao
          reconectar, os grupos se atualizam sozinhos — não exclua o monitorador.
        </span>
        <Link
          to={workspacePath(slug, 'monitoradores')}
          className="mt-2 inline-flex min-h-11 items-center font-medium underline underline-offset-2 md:min-h-0"
        >
          Reconectar agora
        </Link>
      </FormAlert>
    </div>
  );
}
